import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as RecurringIcon } from '@assets/images/ScheduleCard/icon-recurring.svg'
import { ReactComponent as StatusIcon } from '@assets/images/ScheduleCard/icon-status.svg'
import { ReactComponent as InsuranceIcon } from '@assets/images/ScheduleCard/icon-insurance.svg'
import {
  ScheduleCard,
  SCHEDULE_CARD_COLORS,
  SCHEDULE_CARD_COLOR_LABELS,
  SCHEDULE_CARD_DEMO_PATIENT,
  SCHEDULE_CARD_DEMO_TIME,
  SCHEDULE_CARD_DEMO_TYPE,
  type ScheduleCardColor,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ScheduleCardShowcase.css'

const DEMO_HEADER_ICONS = (
  <>
    <RecurringIcon aria-hidden="true" />
    <StatusIcon aria-hidden="true" />
    <InsuranceIcon aria-hidden="true" />
  </>
)

const ScheduleCardShowcase: React.FC = () => {
  const [color, setColor] = useState<ScheduleCardColor>('pale-green')
  const [timeRange, setTimeRange] = useState(SCHEDULE_CARD_DEMO_TIME)
  const [patientName, setPatientName] = useState(SCHEDULE_CARD_DEMO_PATIENT)
  const [appointmentType, setAppointmentType] = useState(
    SCHEDULE_CARD_DEMO_TYPE,
  )
  const [showHeaderIcons, setShowHeaderIcons] = useState(true)
  const [showAppointmentType, setShowAppointmentType] = useState(true)
  const rowRefs = useRef<
    Partial<Record<ScheduleCardColor, HTMLDivElement | null>>
  >({})

  useEffect(() => {
    rowRefs.current[color]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [color])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'schedule-card-color',
            label: 'Provider color',
            control: (
              <select
                id="schedule-card-color"
                className="ui-field-control"
                value={color}
                onChange={(event) =>
                  setColor(event.target.value as ScheduleCardColor)
                }
              >
                {SCHEDULE_CARD_COLORS.map((option) => (
                  <option key={option} value={option}>
                    {SCHEDULE_CARD_COLOR_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'schedule-card-header-icons',
            label: 'Header icons',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="schedule-card-header-icons"
                  type="checkbox"
                  checked={showHeaderIcons}
                  onChange={(event) => setShowHeaderIcons(event.target.checked)}
                />
                <span>Show status icons</span>
              </label>
            ),
          },
          {
            id: 'schedule-card-show-type',
            label: 'Appointment type',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="schedule-card-show-type"
                  type="checkbox"
                  checked={showAppointmentType}
                  onChange={(event) =>
                    setShowAppointmentType(event.target.checked)
                  }
                />
                <span>Visible</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'schedule-card-time',
            label: 'Time range',
            control: (
              <input
                id="schedule-card-time"
                className="ui-field-control"
                type="text"
                value={timeRange}
                onChange={(event) => setTimeRange(event.target.value)}
              />
            ),
          },
          {
            id: 'schedule-card-patient',
            label: 'Patient name',
            control: (
              <input
                id="schedule-card-patient"
                className="ui-field-control"
                type="text"
                value={patientName}
                onChange={(event) => setPatientName(event.target.value)}
              />
            ),
          },
          {
            id: 'schedule-card-type',
            label: 'Appointment type',
            control: (
              <input
                id="schedule-card-type"
                className="ui-field-control"
                type="text"
                value={appointmentType}
                disabled={!showAppointmentType}
                onChange={(event) => setAppointmentType(event.target.value)}
              />
            ),
          },
        ],
      },
    ],
    [
      color,
      timeRange,
      patientName,
      appointmentType,
      showHeaderIcons,
      showAppointmentType,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Schedule card"
        description="Appointment calendar block used in Day/Week views. Color follows the provider palette."
        stageClassName="ui-preview__stage--wide schedule-card-showcase__stage-wrap"
      >
        <div className={`schedule-card-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Same structure as Appointment Day/Week cards: time header, status
              icons, patient, and appointment type. Card colors stay the same;
              mode only changes the page/section background.
            </p>
            <div className="schedule-card-showcase__live">
              <ScheduleCard
                color={color}
                timeRange={timeRange}
                patientName={patientName}
                appointmentType={appointmentType}
                showAppointmentType={showAppointmentType}
                headerIcons={showHeaderIcons ? DEMO_HEADER_ICONS : undefined}
              />
            </div>
          </section>

          <div className="schedule-card-showcase__matrix">
            <h3 className="schedule-card-showcase__matrix-title">
              Provider colors
            </h3>
            <div className="schedule-card-showcase__grid">
              {SCHEDULE_CARD_COLORS.map((colorOption) => (
                <div
                  key={colorOption}
                  ref={(element) => {
                    rowRefs.current[colorOption] = element
                  }}
                  className={`schedule-card-showcase__cell${
                    colorOption === color
                      ? ' schedule-card-showcase__cell--selected'
                      : ''
                  }`}
                >
                  <span className="schedule-card-showcase__cell-label">
                    {SCHEDULE_CARD_COLOR_LABELS[colorOption]}
                  </span>
                  <ScheduleCard
                    color={colorOption}
                    timeRange={timeRange}
                    patientName={patientName}
                    appointmentType={appointmentType}
                    showAppointmentType={showAppointmentType}
                    headerIcons={
                      showHeaderIcons ? DEMO_HEADER_ICONS : undefined
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="ScheduleCard"
        sections={propertySections}
      />
    </>
  )
}

export default ScheduleCardShowcase
